import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe'
import { locales } from '@/constants/locales'

type RoomIsolationHeadersInput = {
  /** The room is in the COI room list (`isCoiHub`). */
  isCoiRoom: boolean
  hasFull3d: boolean
  userAgent: string
}

/** The only header combinations a room response may carry for cross-origin isolation. */
export type RoomIsolationHeaders =
  | Record<string, never>
  | {
      'Cross-Origin-Embedder-Policy': 'require-corp'
      'Cross-Origin-Resource-Policy': 'cross-origin'
    }

const isMobileUserAgent = (userAgent: string) =>
  /Android|webOS|iPhone|iPad|iPod|IEMobile|Opera Mini|Mobile/i.test(userAgent)

// Document-Isolation-Policy shipped in Chromium 137 on desktop and 146 on Android; Android WebView has none.
const DIP_MIN_CHROMIUM = 137
const DIP_MIN_CHROMIUM_ANDROID = 146

// Must match CloudFront `isDipCapable` (genesis-cloudfront-function genesis-response.js): a COEP parent embedding a DIP iframe blocks the iframe.
const isDipCapable = (userAgent: string) => {
  if (/iPad|iPhone|iPod|CriOS|FxiOS|EdgiOS/.test(userAgent)) return false
  const chromiumMajor = Number(/(?:Chrome|Chromium)\/(\d+)/.exec(userAgent)?.[1] ?? 0)
  if (!chromiumMajor) return false
  const isAndroid = userAgent.includes('Android')
  if (isAndroid && userAgent.includes('; wv)')) return false
  return chromiumMajor >= (isAndroid ? DIP_MIN_CHROMIUM_ANDROID : DIP_MIN_CHROMIUM)
}

export const resolveRoomIsolationHeaders = ({
  isCoiRoom,
  hasFull3d,
  userAgent,
}: RoomIsolationHeadersInput): RoomIsolationHeaders => {
  if (!isCoiRoom || (isMobileUserAgent(userAgent) && !hasFull3d)) return {}

  // DIP-capable browsers isolate only the world iframe (DIP is sent by CloudFront), so the parent stays non-isolated.
  if (isDipCapable(userAgent)) return {}

  return {
    'Cross-Origin-Embedder-Policy': 'require-corp',
    'Cross-Origin-Resource-Policy': 'cross-origin',
  }
}

/**
 * Whether a document load of this room is cross-origin isolated: the same rule `proxy.ts` uses to add
 * `Cross-Origin-Embedder-Policy` (`resolveRoomIsolationHeaders`); no request or response header is read.
 */
export const isRoomCrossOriginIsolated = (input: RoomIsolationHeadersInput) =>
  'Cross-Origin-Embedder-Policy' in resolveRoomIsolationHeaders(input)

// The isolation headers depend on the room and `full3d` only; the locale prefix is optional (`as-needed`).
const toIsolationKey = (url: URL) => {
  const segments = url.pathname.split('/').filter(Boolean)
  if ((locales as readonly string[]).includes(segments[0])) segments.shift()

  return `/${segments.join('/')}|${url.searchParams.has(IFRAME_SEARCH_PARAM_KEYS.FULL3D)}`
}

type DocumentNavigationInput = {
  /**
   * URL the browser loaded this document with (its first page, before any client navigation), or `null`
   * when the browser does not report it. Its response headers set the document's isolation.
   */
  documentLoadUrl: URL | null
  /** `self.crossOriginIsolated`: set by the `documentLoadUrl` response and kept across client navigations. */
  isDocumentCrossOriginIsolated: boolean
  /** Room the router is rendering now, reached by a client navigation when it differs from `documentLoadUrl`. */
  targetRoomUrl: URL
  /** Whether a document load of the target room is isolated (`isRoomCrossOriginIsolated`). */
  isTargetRoomCrossOriginIsolated: boolean
  isFramed: boolean
}

/**
 * A client navigation keeps the document's isolation, so a target room reached that way needs a full
 * document navigation when the document's isolation differs from the target room's (CONNECT-6329). The
 * room the document was loaded for already got its own headers and is never navigated again, so this
 * cannot loop. A framed document's isolation also depends on the embedding page, so it keeps client
 * navigation.
 */
export const needsDocumentNavigation = ({
  documentLoadUrl,
  isDocumentCrossOriginIsolated,
  targetRoomUrl,
  isTargetRoomCrossOriginIsolated,
  isFramed,
}: DocumentNavigationInput) =>
  !isFramed &&
  documentLoadUrl !== null &&
  toIsolationKey(documentLoadUrl) !== toIsolationKey(targetRoomUrl) &&
  isDocumentCrossOriginIsolated !== isTargetRoomCrossOriginIsolated
